import { documentType, parsePhoneParts, toIsoDate } from './masks.js';
import { loadCardCrypto } from './lazyModules.js';
import { resolveProcessingCostAmount } from './processingCost.js';
import { buildCustomFieldsPayload } from './customFields.js';

export function buildDonorPayload(config, formData) {
  const donorInfo = formData.donorInfo || {};
  const document = onlyNumbers(donorInfo.Document || '');
  const docType = documentType(document);
  const isCompany = docType === 'Cnpj';
  const name = donorInfo.Name || '';

  return {
    campaignId: config.CampaignId,
    document,
    documentType: docType,
    personal: isCompany ? null : { name, birthDate: toIsoDate(donorInfo.BirthDate) },
    company: isCompany ? { corporateName: name, fantasyName: null, responsibleName: donorInfo.ResponsibleName?.trim() || null } : null,
    address: buildAddressPayload(formData.address),
    email: donorInfo.Email || null,
    phones: donorInfo.Phone ? [buildPhonePayload(donorInfo.Phone, donorInfo.PhoneCountry)] : [],
    agreement: buildAgreementPayload(donorInfo.CommunicationOptIn === true),
    tracking: buildTrackingPayload(),
    communicationId: readCommunicationReference(),
    customFields: emptyToNull(buildCustomFieldsPayload(config, formData.customFieldValues, 'Donor'))
  };
}

const COMMUNICATION_REFERENCE_PARAM = 'tm-ref';
const GUID_PATTERN = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

function readCommunicationReference() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }

  const value = (new URLSearchParams(window.location.search).get(COMMUNICATION_REFERENCE_PARAM) || '').trim();
  return GUID_PATTERN.test(value) ? value : null;
}

function buildTrackingPayload() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const readParam = (key) => {
    const value = params.get(key);
    return value && value.trim() ? value.trim() : null;
  };

  return {
    utmCampaign: readParam('utm_campaign'),
    utmSource: readParam('utm_source'),
    utmMedium: readParam('utm_medium'),
    utmContent: readParam('utm_content'),
    utmTerm: readParam('utm_term'),
    pageUrl: window.location.href || null
  };
}

export async function buildDonationPayload(config, formData) {
  const card = formData.paymentMethod === 'CreditCard'
    ? await buildCardPayload(formData.paymentDetails || {}, config)
    : null;

  return {
    amount: Math.round(Number(formData.amount || 0)),
    paymentMethod: formData.paymentMethod,
    frequency: formData.frequencyId,
    paymentDay: new Date().getDate(),
    card,
    bankAccount: formData.paymentMethod === 'DebitAccount' ? buildBankAccountPayload(formData.paymentDetails || {}) : null,
    projects: selectionToProjects(formData.selection),
    customFields: emptyToNull({ ...formData.customFields, ...buildCustomFieldsPayload(config, formData.customFieldValues, 'Donation') }),
    upsellAccepted: formData.upsellAccepted,
    processingCostAmount: resolveProcessingCostAmount(config, formData)
  };
}

function buildAgreementPayload(optIn) {
  return {
    receiveEmails: optIn,
    receiveCalls: optIn,
    receiveSms: optIn,
    receiveWhatsApps: optIn,
    receiveLetters: optIn,
    receiveDirectMails: optIn
  };
}

function buildAddressPayload(address) {
  if (!address || Object.values(address).every(value => !String(value || '').trim())) {
    return null;
  }

  return {
    city: address.City || null,
    state: address.State || null,
    neighbour: address.Neighbour || null,
    address: address.Street || null,
    number: address.Number || null,
    zipCode: onlyNumbers(address.ZipCode || ''),
    complement: address.Complement || null,
    reference: address.Reference || null
  };
}

function buildPhonePayload(rawPhone, country) {
  const { countryCode, areaCode, number } = parsePhoneParts(rawPhone, country);
  return {
    countryCode,
    areaCode,
    number,
    description: null,
    type: 'Mobile'
  };
}

function buildBankAccountPayload(details) {
  if (!details.gateway) return null;
  const ownerDocument = onlyNumbers(details.ownerDocument || '');
  return {
    gateway: details.gateway,
    ownerName: details.ownerName || '',
    ownerDocument,
    ownerDocumentType: documentType(ownerDocument) || 'Cpf',
    agency: onlyNumbers(details.agency || ''),
    agencyDigit: details.agencyDigit || '',
    accountNumber: onlyNumbers(details.accountNumber || ''),
    accountDigit: details.accountDigit || '',
    operationType: details.operationType || null
  };
}

async function buildCardPayload(details, config) {
  if (!details.cardNumber) {
    return null;
  }

  const [month, year] = String(details.expiry || '').split('/');
  const number = onlyNumbers(details.cardNumber);
  const securityCode = details.cvv || '';

  const payload = {
    holderName: details.cardName || '',
    expirationMonth: Number(month || 0),
    expirationYear: Number(year ? `20${year}` : 0),
    brand: details.cardBrand || null,
    firstDigits: number.slice(0, 6),
    lastDigits: number.slice(-4)
  };

  const publicKey = config?.TokenExPublicKey;
  if (!publicKey) {
    throw new Error('Não foi possível proteger os dados do cartão. Recarregue a página e tente novamente.');
  }

  const { encryptCardForTokenEx } = await loadCardCrypto();
  payload.encryptedCard = encryptCardForTokenEx(publicKey, { number, securityCode });

  return payload;
}

function emptyToNull(values) {
  return Object.keys(values).length > 0 ? values : null;
}

function onlyNumbers(value) {
  return String(value || '').replace(/\D/g, '');
}

/**
 * Collapses the picker's selection into the donation payload, merging entries
 * that point at the same project — the backend rejects duplicated project ids.
 * Outside the picker the selection is always empty, so this yields `[]`.
 */
function selectionToProjects(selection) {
  const byProject = new Map();

  for (const entry of selection || []) {
    if (!entry.projectId) continue;
    byProject.set(entry.projectId, (byProject.get(entry.projectId) || 0) + (entry.amount || 0));
  }

  return Array.from(byProject, ([projectId, amount]) => ({ projectId, amount }));
}
